import { claude, requireKey, requireKeyAsync, skill } from '../api.js';
import {
  ANALYSIS_KEYS,
  ANALYSIS_STEPS_EXISTING,
  genConcept,
  makeAnalysisChunkHandler,
  startLoadingSteps,
  startLoadingStepsWithTimer,
  stopLoadingSteps,
} from '../build/concept.js';
import { blobToTiles, buildScrapeMessage, hydrateBrief, labelTiles } from '../build/helpers.js';
import { $, captureInspoScreenshot, showErr } from '../dom.js';
import { EXISTING_OPTIONS, getResetState, pathToScreen } from '../logic/existing.ts';
import { discoverPages } from '../logic/page-analysis.ts';
import {
  buildKeepNote,
  buildRebrandNote,
  buildRefreshNote,
  generateCopywritingBrief,
} from '../prompts/existing.js';
import { ST } from '../state.js';

// ── EXISTING-SITE FLOW ────────────────────────────────────────────
// Path routing, URL analysis, keep/refresh/rebrand concept generation.
// Pure logic delegated to logic/existing.ts

/**
 * Route the user to the appropriate onboarding path.
 * @param {'questions'|'existing'|'docs'} path - Selected onboarding path
 */
function pickPath(path) {
  ST.startPath = path;
  G.go(pathToScreen(path));
}

/**
 * Reset all application state and return to the start screen.
 * Uses getResetState() from logic/existing.ts for the pure state reset,
 * then clears timers (side effects) and navigates.
 */
function startOver() {
  if (!confirm('Start over? This will clear your current progress.')) {
    return;
  }
  // Clear active timers (side effects — can't be in pure logic)
  if (ST.autoBuildTimer) {
    clearTimeout(ST.autoBuildTimer);
    ST.autoBuildTimer = null;
  }
  if (ST.activeStreamTicks?.length) {
    ST.activeStreamTicks.forEach(t => clearInterval(t));
    ST.activeStreamTicks = [];
  }
  if (ST.imgAnalysisTimer) {
    clearTimeout(ST.imgAnalysisTimer);
    ST.imgAnalysisTimer = null;
  }
  // Apply pure state reset
  Object.assign(ST, getResetState());
  ST.siteScreenshot = null;
  ST.stockPromise = null;
  G.go('start');
}

/**
 * Set the existing-site option (keep/refresh/rebrand) and update UI.
 * @param {'keep'|'refresh'|'rebrand'} opt
 */
function setExOpt(opt) {
  ST.existingOpt = opt;
  EXISTING_OPTIONS.forEach(o => {
    const el = $(`ex-opt-${o}`);
    if (el) {
      el.classList.toggle('selected', o === opt);
    }
  });
}

// ── API CALLS (extract to composable during Vue migration) ──
// analyseExisting(), genFromExisting()

/**
 * Scrape the user's existing website, run brand analysis + site structure
 * extraction via Claude AI, then navigate to brief-review.
 */
async function analyseExisting() {
  const url = $('ex-url-inp')?.value?.trim() || ST.existingUrl;
  if (!url) {
    showErr('Please enter your website URL');
    return;
  }
  ST.existingUrl = url;
  const btn = $('ex-analyse-btn');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Analysing…';
  }

  try {
    // 1. Scrape the website
    const res = await fetch('/api/scrape', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    const data = await res.json();
    if (data.error && !data.title) {
      showErr(
        'Could not fetch website: ' + (data.error || 'Unknown error') + '. Please check the URL.',
      );
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Analyse →';
      }
      return;
    }

    ST.existingScrape = data;

    // 2. Show loading screen
    G.go('loading');
    startLoadingSteps('Generating your Business DNA', ANALYSIS_STEPS_EXISTING, { url });

    // 3. Run brand AI + screenshot fetch + page discovery in parallel
    await requireKeyAsync();

    const userMsg = buildScrapeMessage(data);
    const skBrand = skill('site-analysis');
    const skStruct = skill('site-structure');

    // Discover pages in parallel (sitemap + nav links) — non-blocking
    const pagesPromise = discoverPages(url, data.rawHtml || '').catch(() => []);

    const screenshotPromise = fetch(`/api/screenshot?url=${encodeURIComponent(url)}`)
      .then(r => (r.ok ? r.blob() : null))
      .then(blob => {
        if (!blob) {
          return null;
        }
        // Show in loading screen preview immediately
        const previewUrl = URL.createObjectURL(blob);
        const previewImg = $('ld-preview-img');
        const previewCard = $('ld-preview-card');
        if (previewImg && previewCard) {
          previewImg.src = previewUrl;
          previewImg.style.display = 'block';
          previewCard.style.display = 'block';
          previewCard.classList.remove('loading');
        }
        return blobToTiles(blob);
      })
      .catch(() => null);

    // Wait for screenshot, then run brand + structure in parallel (both use screenshot)
    const screenshotImg = await screenshotPromise;
    ST.siteScreenshot = screenshotImg;

    const labeledScreenshot = labelTiles(screenshotImg, url);

    // Include scraped logo as separate vision input for brand color analysis
    /** @type {Array<import('../types.ts').Base64ImageInput | import('../types.ts').UrlImageInput>} */
    const brandImgs = [...(labeledScreenshot || [])];
    const scrapedLogo = data.logos?.find(l => l.src);
    if (scrapedLogo) {
      brandImgs.push({
        url: scrapedLogo.src,
        label:
          'Company logo from website \u2014 analyze colors, style, and composition for brand identity extraction',
      });
    }

    const imgNote = [
      labeledScreenshot ? 'A full-page screenshot of the website is provided as images.' : '',
      scrapedLogo
        ? 'The company logo is provided separately \u2014 analyze its colors and style for brand identity extraction.'
        : '',
    ]
      .filter(Boolean)
      .join(' ');
    const brandUserMsg = imgNote ? `${userMsg}\n\n${imgNote}` : userMsg;

    // 4. Brand + Structure AI run in parallel (both get screenshot as vision input)
    const [rawBrand, rawStruct] = await Promise.all([
      claude({
        sys: skBrand.prompt,
        user: brandUserMsg,
        imgs: brandImgs.length ? brandImgs : undefined,
        tokens: skBrand.tokens || 5000,
        model: skBrand.model,
        temp: skBrand.temp,
        effort: skBrand.effort,
        schema: skBrand.schema,
        onChunk: makeAnalysisChunkHandler(ANALYSIS_KEYS),
      }),
      claude({
        sys: skStruct.prompt,
        user: userMsg,
        imgs: labeledScreenshot || undefined,
        tokens: skStruct.tokens || 6000,
        model: skStruct.model,
        temp: skStruct.temp,
        schema: skStruct.schema,
      }),
    ]);

    const mb = JSON.parse(rawBrand);

    // Overlay deterministic extractions as fallbacks (overwrite only if AI missed them)
    if (data.copyright && !mb.copyrightText) {
      mb.copyrightText = data.copyright;
    }
    if (data.logos?.length && !mb.logoUrl) {
      mb.logoUrl = data.logos[0].src;
    }
    // Merge ld+json social links if Claude missed them
    if (data.ldJson?.length) {
      const org = data.ldJson.find(d => d['@type'] === 'Organization');
      if (org?.sameAs?.length && !mb.socialLinks?.length) {
        mb.socialLinks = org.sameAs.map(sUrl => {
          const platform = sUrl.includes('linkedin')
            ? 'linkedin'
            : sUrl.includes('twitter') || sUrl.includes('x.com')
              ? 'twitter'
              : sUrl.includes('facebook')
                ? 'facebook'
                : sUrl.includes('instagram')
                  ? 'instagram'
                  : 'other';
          return `${platform}: ${sUrl}`;
        });
      }
    }

    // 4b. Parse site structure response and attach to brief
    try {
      const structData = JSON.parse(rawStruct);
      mb.siteStructure = Array.isArray(structData)
        ? structData
        : structData.siteStructure || structData.sections || [];
    } catch (structErr) {
      console.warn('Site structure parse failed, proceeding without it:', structErr);
      mb.siteStructure = [];
    }
    mb.source = 'existing';
    mb.sourceUrl = url;
    mb.userEdits = {};

    // Collect images from site-structure sections into mb.images for brief-review UI
    mb.images = [];
    for (const sect of mb.siteStructure || []) {
      for (const img of sect.images || []) {
        if (img.url) {
          mb.images.push({
            src: img.url,
            alt: img.description || '',
            category: sect.type || 'other',
          });
        }
      }
    }

    // If user uploaded a logo, add it and prioritize over AI-detected one
    if (ST.logoImg?.data) {
      const logoSrc =
        ST.logoImg.preview || `data:${ST.logoImg.type || 'image/png'};base64,${ST.logoImg.data}`;
      mb.images.unshift({
        src: logoSrc,
        alt: ST.logoImg.name || 'Logo',
        category: 'logo',
        selected: true,
      });
      mb.logoUrl = logoSrc;
    } else if (mb.logoUrl && !ST.logoUrl) {
      ST.logoUrl = mb.logoUrl;
    }

    ST.marketingBrief = hydrateBrief(mb);

    // Collect discovered pages (parallel fetch started earlier)
    ST.discoveredPages = await pagesPromise;

    stopLoadingSteps();
    G.go('brief-review');
  } catch (e) {
    console.error('analyseExisting failed:', e);
    stopLoadingSteps();
    showErr(`Analysis failed: ${e.message}`);
    G.go('existing');
  }
  if (btn) {
    btn.disabled = false;
    btn.textContent = 'Analyse →';
  }
}

/** Proceed from the existing-site detail screen — requires API key. */
function proceedFromExisting() {
  requireKey(() => {
    genFromExisting();
  });
}

// _genConceptKeepMode removed — keep mode now routes through genConcept() with buildKeepNote()
// This gives keep mode a real designVision + visualBriefs instead of empty placeholders.

/**
 * Generate concept from existing site based on selected option.
 * Keep: visual preferences note. Refresh: copywriting brief + note. Rebrand: direction note.
 */
async function genFromExisting() {
  const opt = ST.existingOpt || 'keep';
  const scrape = ST.existingScrape;
  const btn = $('exd-proceed-btn');

  // Capture inspiration screenshot if a URL was entered
  const inspoUrl = $(`${opt}-inspo`)?.value?.trim() || '';
  let inspo = null;
  if (inspoUrl) {
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Capturing inspiration\u2026';
    }
    inspo = await captureInspoScreenshot(inspoUrl);
    if (inspo) {
      ST.inspoImg = inspo;
    }
  }

  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Generating\u2026';
  }

  if (opt === 'keep') {
    const note = buildKeepNote(scrape);
    genConcept(note, inspo);
  } else if (opt === 'refresh') {
    G.go('loading');
    startLoadingStepsWithTimer('Analyzing your content...', [
      'Reading existing copy',
      'Identifying conversion opportunities',
      'Building copywriting brief',
    ]);
    try {
      const mb = ST.marketingBrief;
      const facts = mb?.facts || ST.extractedFacts || {};
      const brief = await generateCopywritingBrief(scrape, facts);
      ST.copywritingBrief = brief;
      stopLoadingSteps();
      const note = buildRefreshNote(scrape, brief);
      genConcept(note, inspo);
    } catch (e) {
      stopLoadingSteps();
      console.warn('Copywriting brief failed, proceeding without it:', e);
      ST.copywritingBrief = null;
      const note = buildRefreshNote(scrape, null);
      genConcept(note, inspo);
    }
  } else {
    const note = buildRebrandNote(scrape);
    genConcept(note, inspo);
  }
}

// ── DOM RENDERING (becomes Vue <template> during migration) ──
// updateExProceedBtn()

/**
 * Enable/disable the proceed button on the existing-site detail screen.
 * Rebrand mode requires a direction note of >= 20 characters.
 */
function updateExProceedBtn() {
  const btn = $('exd-proceed-btn');
  if (!btn) {
    return;
  }
  if (ST.existingOpt === 'rebrand') {
    const dir = $('rebrand-direction')?.value?.trim() || '';
    const ok = dir.length >= 20;
    btn.disabled = !ok;
    btn.style.opacity = ok ? '1' : '0.45';
  } else {
    btn.disabled = false;
    btn.style.opacity = '1';
  }
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  analyseExisting,
  genFromExisting,
  pickPath,
  proceedFromExisting,
  setExOpt,
  startOver,
  updateExProceedBtn,
};
